<template>
  <div
      class="sticky-offer"
      ref="container"
      @click="onClick"
      :class="{
        '--desktop': $q.screen.gt.md,
        '--mobile': !$q.screen.gt.md,
      }"
  >

    <div class="c-inner">
      <template v-if="$q.screen.gt.sm">
        <PulseButton
            v-if="$q.screen.gt.sm"
            class="c-pulse-btn q-mr-sm"
            gradientStart="#e19e3c"
            gradientEnd="#eeba64"
            pulseColor="rgba(206, 135, 20, 0.7)"
            sparkleColor="#000000"
            round
            size="18px"
            icon-size="26px"
            :hovered="isHovered"
            :icon="offer.STICKY_ICON!"
        />
        <q-btn
            :icon="ICONS.close"
            color="grey-6"
            class="c-closer-desktop"
            round
            dense
            size="10px"
            v-if="isHoveredDelay || $q.screen.lt.md"
            @click.stop.prevent="onHide"
        />
        <div
            class="c-title s-font-sm text-weight-medium"
            v-html="offer.STICKY_TITLE"
        />
      </template>
      <template v-else>
        <div class="c-icon-mobile">
          <q-icon :name="offer.STICKY_ICON!" size="20px" color="primary"/>
        </div>
        <q-btn
            :icon="ICONS.close"
            color="secondary"
            class="c-closer-mobile"
            round
            dense
            size="9px"
            @click.stop.prevent="onHide"
            v-if="wasOpened"
        />
        <div
            class="c-title s-font-sm text-weight-medium"
            v-html="offer.STICKY_TITLE"
        />
      </template>
    </div>

  </div>

</template>

<script setup lang="ts">
import {computed, ref, toRefs, useTemplateRef, watch} from "vue";
import {OfferModel, useOfferStore} from "@/modules/offer/store/offer";
import PulseButton from "@/components/Ui/PulseButton.vue";
import {useElementHover} from "@vueuse/core";
import {useRouter} from "vue-router";
import {useQuasar} from "quasar";
import {ICONS} from "@/assets/icons";

const props = withDefaults(defineProps<{
  offer: OfferModel;
}>(), {});

const {offer} = toRefs(props);

const $q = useQuasar();

const containerRef = useTemplateRef('container')
const isHovered = useElementHover(containerRef)
const isHoveredDelay = ref<boolean>(false)
const isHoveredDelayTimeout = ref<any>()

const offerStore = useOfferStore()

watch(isHovered, () => {
  clearTimeout(isHoveredDelayTimeout.value)
  isHoveredDelayTimeout.value = setTimeout(() => {
    isHoveredDelay.value = isHovered.value
  }, 500)
})

const router = useRouter()

const wasOpened = computed(() => !!offerStore.offersStickyOpened[offer.value.ID])

const onClick = () => {
  offerStore.offersStickyOpen(offer.value.ID)
  const action = offer.value.ACTIONS_WEB[0]
  if (action) {
    if (action.url) {
      router.push(action.url)
    }
  }
}

const onHide = () => {
  router.push('/offer/' + offer.value.ID + '/hide')
}

</script>

<style lang="scss" scoped>
.sticky-offer {
  padding: 4px 0;

  .c-inner {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    background-color: #faf1e2;
    border-radius: 34px;
    box-shadow: 0 5px 15px -5px rgba(34, 60, 80, 0.2);
    border: 1px solid $primary-lighter;
    cursor: pointer;
    position: relative;
  }

  &.--desktop {
    .c-inner {
      padding: 0 16px 0 0;
    }
  }

  &.--mobile {
    .c-inner {
      padding: 5px 8px;
    }
  }
}

.c-icon-mobile {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border: 2px solid var(--q-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 8px;
  animation: pulse-border 2s ease-in-out infinite;

  .q-icon {
    animation: rotate-icon 8s linear infinite;
  }
}

.c-title {
  white-space: nowrap;
  line-height: 1.2;
  color: #333;
}

.c-pulse-btn {
  flex-shrink: 0;
}

.c-closer-desktop {
  position: absolute;
  top: -17px;
  right: -17px;
}

.c-closer-mobile {
  position: fixed;
  top: 0px;
  right: -30px;
  z-index: 2;
}

@keyframes pulse-border {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(var(--q-primary-rgb), 0.5);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(var(--q-primary-rgb), 0);
  }
}

@keyframes rotate-icon {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
